import React from 'react';
import { X, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { LeaseAnalysis } from '../utils/leaseCalculator';
import { moneyFactorToAPR } from '../utils/leaseCalculator';

interface ComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentLease: {
    data: any;
    analysis: LeaseAnalysis;
  };
  comparisonLease: {
    data: any;
    analysis: LeaseAnalysis;
  };
}

export function ComparisonModal({ isOpen, onClose, currentLease, comparisonLease }: ComparisonModalProps) {
  if (!isOpen) return null;

  const compareValues = (current: number, comparison: number) => {
    const diff = current - comparison;
    const percentDiff = (diff / comparison) * 100;
    
    if (Math.abs(diff) < 0.01) return {
      icon: <Minus className="w-4 h-4 text-gray-500" />,
      color: 'text-gray-500',
      text: 'No change'
    };
    
    if (diff > 0) return {
      icon: <TrendingUp className="w-4 h-4 text-red-500" />,
      color: 'text-red-500',
      text: `+${percentDiff.toFixed(1)}%`
    };
    
    return {
      icon: <TrendingDown className="w-4 h-4 text-green-500" />,
      color: 'text-green-500',
      text: `${percentDiff.toFixed(1)}%`
    };
  };

  const ComparisonRow = ({ label, current, comparison, format = (n: number) => n.toString(), inverse = false }: {
    label: string;
    current: number;
    comparison: number;
    format?: (n: number) => string;
    inverse?: boolean;
  }) => {
    const comparison_ = inverse ? compareValues(comparison, current) : compareValues(current, comparison);
    
    return (
      <div className="grid grid-cols-3 gap-4 py-3 border-b border-gray-100">
        <div className="text-gray-600">{label}</div>
        <div className="text-right font-medium">{format(current)}</div>
        <div className="flex items-center justify-end space-x-2">
          <span className={`text-sm ${comparison_.color}`}>
            {comparison_.text}
          </span>
          {comparison_.icon}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Lease Comparison</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-6">
          <div className="bg-gray-50 rounded-xl p-4">
            <ComparisonRow
              label="Monthly Payment"
              current={currentLease.analysis.monthlyPayment}
              comparison={comparisonLease.analysis.monthlyPayment}
              format={(n) => `$${n.toFixed(2)}`}
            />
            
            <ComparisonRow
              label="Total Cost"
              current={currentLease.analysis.totalCost}
              comparison={comparisonLease.analysis.totalCost}
              format={(n) => `$${n.toLocaleString()}`}
            />
            
            <ComparisonRow
              label="APR"
              current={moneyFactorToAPR(Number(currentLease.data.moneyFactor))}
              comparison={moneyFactorToAPR(Number(comparisonLease.data.moneyFactor))}
              format={(n) => `${n.toFixed(2)}%`}
            />
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-medium text-gray-900 mb-3">Price Comparison</h3>
            
            <ComparisonRow
              label="MSRP"
              current={Number(currentLease.data.msrp)}
              comparison={Number(comparisonLease.data.msrp)}
              format={(n) => `$${n.toLocaleString()}`}
            />
            
            <ComparisonRow
              label="Selling Price"
              current={Number(currentLease.data.sellingPrice)}
              comparison={Number(comparisonLease.data.sellingPrice)}
              format={(n) => `$${n.toLocaleString()}`}
            />
            
            <ComparisonRow
              label="Residual Value"
              current={Number(currentLease.data.residualValue)}
              comparison={Number(comparisonLease.data.residualValue)}
              format={(n) => `$${n.toLocaleString()}`}
            />
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-medium text-gray-900 mb-3">Terms & Fees</h3>
            
            <ComparisonRow
              label="Term"
              current={Number(currentLease.data.term)}
              comparison={Number(comparisonLease.data.term)}
              format={(n) => `${n} months`}
            />
            
            <ComparisonRow
              label="Down Payment"
              current={Number(currentLease.data.downPayment)}
              comparison={Number(comparisonLease.data.downPayment)}
              format={(n) => `$${n.toLocaleString()}`}
            />
            
            <ComparisonRow
              label="Total Fees"
              current={Number(currentLease.data.fees)}
              comparison={Number(comparisonLease.data.fees)}
              format={(n) => `$${n.toLocaleString()}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}